/**
 * Timeline Slider - Powered by Swiper.js
 */

class TimelineSlider {
  constructor(section) {
    this.section = section;
    this.swiperEl = section.querySelector('.timeline-swiper');
    this.scrollbarContainer = section.querySelector('.timeline-scrollbar');
    this.prevBtn = section.querySelector('.timeline-slider__nav--prev');
    this.nextBtn = section.querySelector('.timeline-slider__nav--next');

    // Settings from data attributes
    this.enableLoop = this.swiperEl.dataset.loop === 'true';
    this.showArrows = this.swiperEl.dataset.showArrows === 'true';
    this.cardsCount = parseInt(this.swiperEl.dataset.cardsCount) || 0;

    // Cursor elements
    this.cursorElement = null;
    this.cursorDirection = null;
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    this.swiper = null;

    // Debug flag
    this.debug = false;
    this.log('Constructor called', { enableLoop: this.enableLoop, showArrows: this.showArrows, cardsCount: this.cardsCount });

    this.init();
  }

  log(...args) {
    if (this.debug) {
      console.log('[TimelineSlider]', ...args);
    }
  }

  init() {
    this.log('Initializing...');
    this.initSwiper();
    if (!this.isTouch) {
      this.initCursor();
    }
    this.bindEvents();
    this.log('Initialization complete');
  }

  initSwiper() {
    const gap = parseInt(getComputedStyle(this.section).getPropertyValue('--timeline-card-gap')) || 5;
    this.log('Initializing Swiper with gap:', gap);

    const swiperConfig = {
      direction: 'horizontal',
      rtl: true,
      slidesPerView: 3.4,
      spaceBetween: gap,
      loop: true,
      speed: 400,
      // 👇 MUY importante
      initialSlide: 0,

      breakpoints: {
        1041: {
          slidesPerView: 3.4,
        },
        768: {
          slidesPerView: 2.4,
          centeredSlides: true,
        },
        280: {
          slidesPerView: 1.4,
          centeredSlides: true,
        }
      },

      scrollbar: this.scrollbarContainer ? {
        el: this.scrollbarContainer,
        draggable: true,
      } : false,

      navigation: this.showArrows ? {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      } : false,

      on: {
        init: (swiper) => {
          // 🔥 fuerza recalculo RTL + loop
          swiper.update();
          swiper.slideToLoop(1, 0, false);
        }
      }
    };


    this.log('Swiper config:', swiperConfig);
    this.swiper = new Swiper(this.swiperEl, swiperConfig);
    this.log('Swiper instance created:', this.swiper);
  }

  updateScrollbarStyles() {
    // Swiper handles the scrollbar drag element position automatically
    // CSS variables handle the styling
  }

  updateNavigation() {
    if (!this.prevBtn || !this.nextBtn) return;

    if (this.enableLoop) {
      this.prevBtn.style.opacity = '1';
      this.prevBtn.style.pointerEvents = 'auto';
      this.nextBtn.style.opacity = '1';
      this.nextBtn.style.pointerEvents = 'auto';
      return;
    }

    const isBeginning = this.swiper.isBeginning;
    const isEnd = this.swiper.isEnd;

    this.prevBtn.style.opacity = isBeginning ? '0.3' : '1';
    this.prevBtn.style.pointerEvents = isBeginning ? 'none' : 'auto';
    this.nextBtn.style.opacity = isEnd ? '0.3' : '1';
    this.nextBtn.style.pointerEvents = isEnd ? 'none' : 'auto';
  }

  initCursor() {
    this.cursorElement = document.createElement('div');
    this.cursorElement.className = 'timeline-cursor';
    // Separar blur en elemento HTML (Safari no soporta foreignObject con backdrop-filter)
    this.cursorElement.innerHTML = `
      <div class="timeline-cursor__blur"></div>
      <svg xmlns="http://www.w3.org/2000/svg" width="122" height="122" viewBox="0 0 122 122" fill="none">
        <circle cx="61" cy="61" r="61" fill="#4a4a4a" fill-opacity="0.35"/>
        <circle cx="61" cy="61" r="61" fill="white" fill-opacity="0.08"/>
        <path d="M58 57L65.8377 64.5L58 72" stroke="#111111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    document.body.appendChild(this.cursorElement);
  }

  bindEvents() {
    this.log('Binding events, isTouch:', this.isTouch);

    // Store bound handlers for cleanup
    this.boundHandlers = {};

    if (!this.isTouch && this.cursorElement) {
      this.boundHandlers.mouseEnter = this.onMouseEnter.bind(this);
      this.boundHandlers.mouseLeave = this.onMouseLeave.bind(this);
      this.boundHandlers.mouseMove = this.onMouseMove.bind(this);
      this.boundHandlers.click = this.onClick.bind(this);

      this.swiperEl.addEventListener('mouseenter', this.boundHandlers.mouseEnter);
      this.swiperEl.addEventListener('mouseleave', this.boundHandlers.mouseLeave);
      this.swiperEl.addEventListener('mousemove', this.boundHandlers.mouseMove);
      this.swiperEl.addEventListener('click', this.boundHandlers.click);
    }

    // Add manual navigation buttons if arrows are hidden but cursor navigation enabled
    if (!this.showArrows && !this.isTouch) {
      this.log('Enabling cursor-based navigation');
    }
  }

  removeEvents() {
    if (this.boundHandlers) {
      if (this.boundHandlers.mouseEnter) {
        this.swiperEl.removeEventListener('mouseenter', this.boundHandlers.mouseEnter);
      }
      if (this.boundHandlers.mouseLeave) {
        this.swiperEl.removeEventListener('mouseleave', this.boundHandlers.mouseLeave);
      }
      if (this.boundHandlers.mouseMove) {
        this.swiperEl.removeEventListener('mousemove', this.boundHandlers.mouseMove);
      }
      if (this.boundHandlers.click) {
        this.swiperEl.removeEventListener('click', this.boundHandlers.click);
      }
    }
  }

  onMouseEnter() {
    this.section.classList.add('cursor-active');
    if (this.cursorElement) {
      this.cursorElement.classList.add('is-visible');
    }
  }

  onMouseLeave() {
    this.section.classList.remove('cursor-active');
    if (this.cursorElement) {
      this.cursorElement.classList.remove('is-visible');
    }
    this.swiperEl.classList.remove('cursor-left', 'cursor-right');
    this.cursorDirection = null;
  }

  onMouseMove(e) {
    if (!this.cursorElement) return;

    // Update cursor position
    this.cursorElement.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;

    // Update direction
    const rect = this.swiperEl.getBoundingClientRect();
    const isLeft = (e.clientX - rect.left) < rect.width / 2;
    const newDir = isLeft ? 'left' : 'right';

    if (this.cursorDirection !== newDir) {
      this.cursorDirection = newDir;
      this.cursorElement.classList.toggle('timeline-cursor--left', isLeft);
      this.cursorElement.classList.toggle('timeline-cursor--right', !isLeft);
      this.swiperEl.classList.toggle('cursor-left', isLeft);
      this.swiperEl.classList.toggle('cursor-right', !isLeft);
    }
  }

  onClick(e) {
    this.log('Click event', e.target);

    // Don't navigate if clicking on navigation buttons
    if (e.target.closest('.timeline-slider__nav')) {
      this.log('Clicked on nav button, ignoring');
      return;
    }

    // Don't navigate if swiping/dragging
    if (this.swiper) {
      // Check if swiper is currently animating or was just dragged
      const touchesDiff = this.swiper.touches ? Math.abs(this.swiper.touches.diff) : 0;
      if (touchesDiff > 10) {
        this.log('Was dragging, ignoring click');
        return;
      }
    }

    const rect = this.swiperEl.getBoundingClientRect();
    const isLeft = (e.clientX - rect.left) < rect.width / 2;

    this.log('Click navigation:', isLeft ? 'prev' : 'next');

    if (isLeft) {
      this.swiper.slidePrev();
    } else {
      this.swiper.slideNext();
    }
  }

  destroy() {
    this.log('Destroying instance');

    // Remove event listeners
    this.removeEvents();

    // Destroy Swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Remove cursor element
    if (this.cursorElement && this.cursorElement.parentNode) {
      this.cursorElement.parentNode.removeChild(this.cursorElement);
      this.cursorElement = null;
    }

    // Clear references
    this.section.classList.remove('cursor-active');
    this.swiperEl.classList.remove('cursor-left', 'cursor-right');
  }
}

// Store instances
const timelineInstances = new Map();

// Debug mode (can be enabled from console: window.timelineSliderDebug = true)
window.timelineSliderDebug = false;

// Initialize
function initTimelineSliders() {
  console.log('[TimelineSlider] Initializing sliders...');

  document.querySelectorAll('.timeline-slider-section').forEach(section => {
    if (!timelineInstances.has(section)) {
      const instance = new TimelineSlider(section);
      instance.debug = window.timelineSliderDebug;
      timelineInstances.set(section, instance);
      console.log('[TimelineSlider] Created instance for section:', section.id);
    }
  });

  console.log('[TimelineSlider] Total instances:', timelineInstances.size);
}

// Wait for Swiper to load
function waitForSwiper(callback, maxAttempts = 50) {
  let attempts = 0;
  const check = () => {
    if (typeof Swiper !== 'undefined') {
      console.log('[TimelineSlider] Swiper loaded after', attempts, 'attempts');
      callback();
    } else if (attempts < maxAttempts) {
      attempts++;
      setTimeout(check, 100);
    } else {
      console.error('[TimelineSlider] Swiper failed to load after', maxAttempts, 'attempts');
    }
  };
  check();
}

// Enable debug on all instances
window.enableTimelineSliderDebug = function () {
  window.timelineSliderDebug = true;
  timelineInstances.forEach(instance => {
    instance.debug = true;
  });
  console.log('[TimelineSlider] Debug mode enabled for all instances');
};

// Get instances for debugging
window.getTimelineSliders = function () {
  return timelineInstances;
};

// DOM Ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => waitForSwiper(initTimelineSliders));
} else {
  waitForSwiper(initTimelineSliders);
}

// Shopify editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.timeline-slider-section');
    if (section) {
      waitForSwiper(() => {
        // Destroy old instance if exists
        if (timelineInstances.has(section)) {
          timelineInstances.get(section).destroy();
          timelineInstances.delete(section);
        }
        const instance = new TimelineSlider(section);
        instance.debug = window.timelineSliderDebug;
        timelineInstances.set(section, instance);
      });
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.timeline-slider-section');
    if (section && timelineInstances.has(section)) {
      timelineInstances.get(section).destroy();
      timelineInstances.delete(section);
    }
  });
}
